import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { useAuth } from '../../shared/useAuth.js';
import { useAllowedWarehouses } from '../../shared/warehouseAccess.js';
import { ConfirmModal } from '../pricing/components/ConfirmModal.jsx';

import { useWarehouses, useAllCategories, useCreateCategory, useUpdateCategory } from './hooks/useCategories.js';
import { useProducts } from './hooks/useProducts.js';
import { useCreateProduct, useUpdateProduct, useDeleteProduct } from './hooks/useProductMutations.js';

import { CategoriesPanel } from './components/CategoriesPanel.jsx';
import { CategoryForm } from './components/CategoryForm.jsx';
import { ProductsTable } from './components/ProductsTable.jsx';
import { ProductDrawer } from './components/ProductDrawer.jsx';
import { BulkImportModal } from './components/BulkImportModal.jsx';
import { BulkExportButton } from './components/BulkExportButton.jsx';
import { WarehousesModal } from './components/WarehousesModal.jsx';

export function ProductsScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canCreate = hasPermission('inventory.product.create');
  const canEdit = hasPermission('inventory.product.edit');
  const canManageCats = hasPermission('inventory.category.manage');

  const [warehouseId, setWarehouseId] = useState('all');
  const [categoryId, setCategoryId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: rawWarehouses = [] } = useWarehouses();
  const warehouses = useAllowedWarehouses(rawWarehouses);
  const { items: allCategories } = useAllCategories(warehouses);

  const filteredCategories = useMemo(() => {
    if (warehouseId === 'all') return allCategories;
    return allCategories.filter((c) => String(c.warehouseId?._id || c.warehouseId) === String(warehouseId));
  }, [allCategories, warehouseId]);

  const productsQuery = useProducts({
    q: search || undefined,
    warehouseId: warehouseId === 'all' ? undefined : warehouseId,
    categoryId: categoryId || undefined,
    page,
    limit: 25
  });

  const productsByCategory = useMemo(() => {
    const map = {};
    for (const p of productsQuery.data?.items || []) {
      const id = p.categoryId?._id || p.categoryId;
      if (!id) continue;
      map[id] = (map[id] || 0) + 1;
    }
    return map;
  }, [productsQuery.data]);

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const [catFormOpen, setCatFormOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [warehousesOpen, setWarehousesOpen] = useState(false);
  const canManageWarehouses = hasPermission('inventory.warehouse.manage');

  function openCreateProduct() {
    setEditingProduct(null);
    setDrawerOpen(true);
  }
  function openEditProduct(p) {
    setEditingProduct(p);
    setDrawerOpen(true);
  }

  async function handleProductSubmit(body, opts = {}) {
    if (editingProduct) {
      await updateProduct.mutateAsync(
        { id: editingProduct._id || editingProduct.id, body },
        { onSuccess: () => setDrawerOpen(false) }
      );
    } else {
      await createProduct.mutateAsync(body, {
        onSuccess: () => {
          if (opts.continueAfter) {
            // keep drawer open with reset for new
            setEditingProduct(null);
            setDrawerOpen(true);
          } else {
            setDrawerOpen(false);
          }
        }
      });
    }
  }

  function openCreateCategory() {
    setEditingCat(null);
    setCatFormOpen(true);
  }
  function openEditCategory(c) {
    setEditingCat(c);
    setCatFormOpen(true);
  }
  async function handleCategorySubmit(body) {
    if (editingCat) {
      await updateCategory.mutateAsync(
        { id: editingCat._id || editingCat.id, body },
        { onSuccess: () => setCatFormOpen(false) }
      );
    } else {
      await createCategory.mutateAsync(body, {
        onSuccess: () => setCatFormOpen(false)
      });
    }
  }

  const defaultCatWh = warehouseId !== 'all' ? warehouseId : (warehouses[0]?._id || warehouses[0]?.id);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">المنتجات والأقسام</h2>
          <div className="text-ink-3 text-xs mt-0.5">إدارة الكتالوج، الأقسام، والاستيراد/التصدير</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManageWarehouses ? (
            <button type="button" className="btn" onClick={() => setWarehousesOpen(true)}>
              <Icon name="inventory" size={14} /> إدارة المخازن
            </button>
          ) : null}
          <BulkExportButton />
          {canCreate ? (
            <button type="button" className="btn" onClick={() => setImportOpen(true)}>
              <Icon name="plus" size={14} /> استيراد
            </button>
          ) : null}
          {canCreate ? (
            <button type="button" className="btn btn-primary" onClick={openCreateProduct}>
              <Icon name="plus" size={14} /> منتج جديد
            </button>
          ) : null}
        </div>
      </div>

      {/* Warehouse selector */}
      <Card>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            <button
              type="button"
              onClick={() => { setWarehouseId('all'); setCategoryId(null); setPage(1); }}
              className={clsx(
                'px-3.5 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                warehouseId === 'all' ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
              )}
            >
              الكل
            </button>
            {warehouses.map((w) => {
              const id = w._id || w.id;
              const active = String(warehouseId) === String(id);
              const emoji = w.type === 'cafeteria' ? '🍕' : w.type === 'toys' ? '🧸' : '📦';
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setWarehouseId(id); setCategoryId(null); setPage(1); }}
                  className={clsx(
                    'px-3.5 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                    active ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  {emoji} {w.name}
                </button>
              );
            })}
          </div>

          <div className="relative flex-1 min-w-[200px]">
            <Icon name="search" size={14} className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none" />
            <input
              className="input pr-9 text-[12.5px]"
              placeholder="ابحث باسم المنتج، SKU، أو الباركود..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </Card>

      <div className="grid gap-3" style={{ gridTemplateColumns: '300px minmax(0,1fr)' }}>
        <CategoriesPanel
          categories={filteredCategories}
          productsByCategory={productsByCategory}
          selected={categoryId}
          onSelect={(id) => { setCategoryId(id); setPage(1); }}
          onCreate={openCreateCategory}
          onEdit={openEditCategory}
          canManage={canManageCats}
        />

        <ProductsTable
          products={productsQuery.data?.items || []}
          loading={productsQuery.isLoading}
          page={productsQuery.data?.page || page}
          pages={productsQuery.data?.pages || 1}
          total={productsQuery.data?.total || 0}
          onPageChange={(p) => setPage(Math.max(1, p))}
          onEdit={openEditProduct}
          onDelete={(p) => setConfirmDelete(p)}
          canEdit={canEdit}
        />
      </div>

      <ProductDrawer
        open={drawerOpen}
        product={editingProduct}
        warehouses={warehouses}
        categories={allCategories}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleProductSubmit}
        submitting={createProduct.isPending || updateProduct.isPending}
        saveAndNew
      />

      <CategoryForm
        open={catFormOpen}
        category={editingCat}
        warehouses={warehouses}
        defaultWarehouseId={defaultCatWh}
        onClose={() => setCatFormOpen(false)}
        onSubmit={handleCategorySubmit}
        submitting={createCategory.isPending || updateCategory.isPending}
      />

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        warehouses={warehouses}
        categories={allCategories}
      />

      <WarehousesModal
        open={warehousesOpen}
        onClose={() => setWarehousesOpen(false)}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف منتج"
        message={`هل أنت متأكد من حذف "${confirmDelete?.name || ''}"؟ سيتم تعطيل المنتج (حذف ناعم).`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteProduct.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteProduct.mutateAsync(
            confirmDelete._id || confirmDelete.id,
            { onSuccess: () => setConfirmDelete(null) }
          );
        }}
      />
    </div>
  );
}
